import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PrepaidSettings } from './PrepaidSettings/PrepaidSettings';
import { AuthInfoState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { getAndSetCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { usePaymentList } from '@/hooks/api/payment/usePaymentList';
import { usePrepaidSetting } from '@/hooks/api/prepaid/usePrepaidSetting';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import {
  BillInformation,
  Customer,
  PaymentMethod,
  Prepaid,
} from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';

type PrepaidSettingsPageProps = {
  f7router: Router.Router;
  status?: string;
};

export const PrepaidSettingsPage: FC<PrepaidSettingsPageProps> = ({
  f7router,
  status,
}) => {
  const customerInfo = useStore(store, 'customerInfo') as Required<Customer>;
  const settingsPrevPage = store?.state.settingsPrevPage;
  const prevPage = settingsPrevPage || window.localStorage.getItem('prevPage');
  const prepaidInfo = useStore(
    store,
    'getFirstAdvancePaymentsInfo',
  ) as Required<Prepaid>;
  const billsInfo = useStore(
    store,
    'getBillInfo',
  ) as Required<BillInformation>[];
  const paymentMethods = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as Required<PaymentMethod>[];
  const [isUpdating, setIsUpdating] = useState(false);
  const { isFetched, isFetching } = usePaymentList(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const authInfo = store?.state?.authInfo ?? ({} as AuthInfoState);
  const getStormCustomerId = authInfo?.m_customer_id ?? 0;
  const { bill, payment, isLoading } = usePrepaidSetting(
    customerInfo,
    billsInfo,
    prepaidInfo,
    paymentMethods,
  );
  const customerId = window.localStorage.getItem('m_customer_id');

  const token =
    window.localStorage.getItem('token') === null
      ? undefined
      : window.localStorage.getItem('token');

  const { isLoggedIn, resetIsLoggedIn } = useLoadCustomer(
    f7.views.main.router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
  );
  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  useEffect(() => {
    if (isLoggedIn) {
      handleGmoRegisterAndEditRedirect(currentStatus ?? '', isLoggedIn);
    }
  }, [isLoggedIn, currentStatus]);

  // ページを再レンダリングさせたい際に、別画面で呼び出す用の関数
  const handleIsUpdating = () => {
    setIsUpdating(true);
  };

  const afterGmoRedirect = useCallback(() => {
    if (currentStatus !== undefined && currentStatus !== '') {
      // prevPageをタブバーの有効なメニュー名にマッピング
      const tabMenu =
        prevPage === 'charge_prepaid' || prevPage === 'prepaid'
          ? 'prepaid'
          : prevPage === 'payments'
          ? 'account'
          : prevPage;
      storeDispatch('setMenu', 'menu', tabMenu);
    }

    // GMOステータスURL(/gmo_success, /gmo_cancel)をクリーンアップ
    // → 戻るボタンでループしないようにする
    const cleanupGmoUrl = () => {
      window.localStorage.removeItem('fromPage');
      window.localStorage.removeItem('destination_page');
      window.localStorage.removeItem('destination_step');
      // GMOリダイレクト後はフルページリロードでF7のDOMスタックがリセットされるため、
      // handleClickBackでf7CustomBack()(=router.back())を使うとGMO URLに戻ってしまう。
      // postGmoNavフラグでnavigate()を使うよう指示する。
      window.localStorage.setItem('postGmoNav', 'true');
      window.history.replaceState({}, '', paths.prepaidSetting);
      const routerHistory = f7.view.main.router.history;
      if (routerHistory.length > 0) {
        routerHistory[routerHistory.length - 1] = paths.prepaidSetting;
      }
      window.localStorage.setItem(
        'f7router-view_main-history',
        JSON.stringify(routerHistory),
      );
    };

    switch (currentStatus) {
      case 'gmo_success':
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          resetIsLoggedIn();
          cleanupGmoUrl();
        }
        break;
      case 'gmo_cancel':
        if (!isLoggedIn) return;
        setCurrentStatus(undefined);
        resetIsLoggedIn();
        getAndSetCustomerInfo({
          m_customer_id: getStormCustomerId,
        });
        cleanupGmoUrl();
        break;
      default:
        break;
    }
  }, [currentStatus, isLoggedIn, resetIsLoggedIn]);

  // GMOからのリダイレクト処理
  useEffect(() => {
    afterGmoRedirect();
  }, [afterGmoRedirect]);

  // charge_prepaidからGMO経由で戻った場合、PrepaidSettingsを表示せず
  // charge_prepaidに自動遷移する
  // ストアデータ（残高・決済方法）が読み込まれるまで待ってからリダイレクトする
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!(status && prevPage === 'charge_prepaid')) return;
    // ストアにデータが読み込まれるまで待つ（SPAリロード後はストアが空のため）
    if (!prepaidInfo?.id) return;

    // gmo_cancel: カード登録なし → データ読み込み完了後に遷移
    if (status === 'gmo_cancel') {
      redirectedRef.current = true;
      window.localStorage.removeItem('prevPage');
      window.localStorage.removeItem('fromPage');
      window.localStorage.setItem('postGmoNav', 'true');
      storeDispatch('setMenu', 'menu', 'prepaid');
      f7.view.main.router.navigate(paths.chargePrepaid, { animate: false });
      return;
    }

    // gmo_success: データ更新完了後に遷移
    if (status === 'gmo_success' && paymentMethods.length > 0) {
      redirectedRef.current = true;
      window.localStorage.removeItem('prevPage');
      window.localStorage.removeItem('fromPage');
      window.localStorage.setItem('postGmoNav', 'true');
      storeDispatch('setMenu', 'menu', 'prepaid');
      f7.view.main.router.navigate(paths.chargePrepaid, { animate: false });
    }
  }, [paymentMethods, prepaidInfo, prevPage, status]);

  return (
    <PrepaidSettings
      f7router={f7router}
      status={currentStatus}
      isLoading={isLoading}
      bill={bill}
      prepaid={prepaidInfo}
      payment={payment}
      handleIsUpdating={handleIsUpdating}
      prevPage={prevPage ?? undefined}
    />
  );
};
